import {
  CustomStepCategoryEnum,
  DefaultSurveyCategories,
  DefaultSurveyReasons,
  FunnelInputTypeEnum,
  StepDiscountSettingTypes,
  StepDiscountTypes,
  StepDurationUnits,
  StepElementTypes,
  StepTimeOptionTypes,
} from '../../_models';

const generateTextComponent = (
  labelTitle: string,
  labelSubtitle: string,
  contentTitle: string,
  contentSubtitle: string,
  banner = false
) => ({
  type: StepElementTypes.Textual,
  layout: {
    banner,
    label: {
      title: labelTitle,
      subtitle: labelSubtitle,
    },
    content: {
      title: contentTitle,
      subtitle: contentSubtitle,
    },
  },
});

const generateMediaComponent = (url = '/assets/stepAssets/image-placeholder.svg', enabled = false) => ({
  type: StepElementTypes.Media,
  layout: {
    title: 'Media',
    subtitle:
      'Add visual elements to make your step more engaging. You can show a product image, a fixed asset, or even a video.',
    placeholderImage: url,
    enabled,
  },
});

const generateButtonLabelsComponent = (confirmTitle: string, rejectTitle: string, rejectDisabled = false) => ({
  type: StepElementTypes.Buttons,
  layout: {
    title: 'Custom Button Labels',
    subtitle: 'Add your own custom button labels to make your flow even more intuitive to use.',
    confirmTitle,
    rejectTitle,
    rejectDisabled,
  },
});

const generateSurveyResponses = () => ({
  type: StepElementTypes.SurveyOptions,
  layout: {
    title: 'Survey response options',
    subtitle: 'Add response options that help you understand your customers’ reasons.',
    items: [
      { reason: DefaultSurveyReasons.TooExpensive, category: DefaultSurveyCategories.CostFactor },
      { reason: DefaultSurveyReasons.NotWorkingForMe, category: DefaultSurveyCategories.ProductIssue },
      { reason: DefaultSurveyReasons.TooMuchProduct, category: DefaultSurveyCategories.FrequencyIssue },
      { reason: DefaultSurveyReasons.OrderedWrongItem, category: DefaultSurveyCategories.OrderIssue },
    ],
  },
});

const generateResponseScreenComponent = () => ({
  type: StepElementTypes.ResponseScreen,
  layout: {
    title: 'Confirmation Message',
    subtitle: 'Customize the confirmation message shown once the customer accepts the offer.',
    responseTitle: {
      title: 'Heading',
      subtitle: 'This is the heading shown to the customer after they complete the step. ',
    },
    response: {
      title: 'Message',
      subtitle: 'Use this space to briefly explain what just happened and what the customer can expect next.',
    },
  },
});

const generateCancellationComponent = () => ({
  type: StepElementTypes.Cancellation,
});

const generateLayoutComponent = (subtitle: string) => ({
  type: StepElementTypes.Layout,
  layout: {
    subtitle,
  },
});

export const stepLayout = {
  /* SURVEYS */
  [CustomStepCategoryEnum.CancelOrderSurvey]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction to be displayed to users wishing to leave.'
    ),
    generateMediaComponent(),
    generateSurveyResponses(),
    generateLayoutComponent('Choose how customers will select their survey response'),
    generateButtonLabelsComponent('Submit Button', null, true),
  ],
  [CustomStepCategoryEnum.CancelSubSurvey]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction to be displayed to users wishing to leave.'
    ),
    generateMediaComponent(),
    generateSurveyResponses(),
    generateLayoutComponent('Choose how customers will select their survey response'),
    generateButtonLabelsComponent('Submit Button', null, true),
  ],
  [CustomStepCategoryEnum.CancelTrialSurvey]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction to be displayed to users wishing to leave.'
    ),
    generateMediaComponent(),
    generateSurveyResponses(),
    generateLayoutComponent('Choose how customers will select their survey response'),
    generateButtonLabelsComponent('Submit Button', null, true),
  ],
  [CustomStepCategoryEnum.ReturnSurvey]: [
    generateTextComponent(
      'Title',
      'This is the main heading users will see.',
      'Description',
      'A short introduction to be displayed to users wishing to leave.'
    ),
    generateMediaComponent(),
    generateSurveyResponses(),
    generateLayoutComponent('Choose how customers will select their survey response'),
    generateButtonLabelsComponent('Submit Button', null, true),
  ],
  [CustomStepCategoryEnum.Survey]: [
    generateTextComponent(
      'Title',
      'This is the main heading users will see.',
      'Description',
      'A short introduction to be displayed to users wishing to leave.'
    ),
    generateMediaComponent(),
    generateLayoutComponent('Choose how customers will select their survey response'),
    generateSurveyResponses(),
    generateButtonLabelsComponent('Submit Button', null, true),
  ],
  [CustomStepCategoryEnum.ResolveSurvey]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction to be displayed to users requesting a refund.'
    ),
    generateMediaComponent(),
    generateSurveyResponses(),
    generateLayoutComponent('Choose how customers will select their survey response'),
    generateButtonLabelsComponent('Submit Button', null, true),
  ],

  /* DOWNSELLS */
  [CustomStepCategoryEnum.PercentageDownsell]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    {
      type: StepElementTypes.Discount,
      layout: {
        title: 'Offer a discount',
        subtitle: 'Example: 10%.',
        inputTitle: 'Discount percentage',
        type: StepDiscountTypes.Percentage,
      },
    },
    generateMediaComponent(),
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.FixedAmountDownsell]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    {
      type: StepElementTypes.Discount,
      layout: {
        title: 'Offer a discount',
        subtitle: 'Example: $10.',
        inputTitle: 'Discount Amount',
        type: StepDiscountTypes.Amount,
      },
    },
    generateMediaComponent(),
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.PauseSub]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.TimeDelay,
      layout: {
        title: 'Pause Settings',
        subtitle: 'Define how long the customer’s subscription will be paused if they accept this offer.',
      },
    },
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [CustomStepCategoryEnum.RefundOrder]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.DiscountSettings,
      layout: {
        title: 'Refund Settings',
        subtitle: 'Set up how you’d like to calculate and issue a partial refund for this item or order.',
        type: StepDiscountSettingTypes.DiscountRefund,
      },
    },
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.DiscountFutureOrder]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.DiscountSettings,
      layout: {
        title: 'Discount Settings',
        subtitle: 'Configure how much of a discount to apply and how it’s calculated for upcoming subscription cycles.',
        type: StepDiscountSettingTypes.FutureOrder,
      },
    },
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.PauseSurvey]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction to be displayed to users wishing to pause subscription.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.TimeOptions,
      layout: {
        title: 'Pause Option',
        subtitle:
          'Set up multiple pause durations for the customer to choose from. for example, 30, 60, or 90 days. This lets customers select what works best for them while still reducing churn.',
        type: StepTimeOptionTypes.PauseOptions,
        items: [
          { value: 15, unit: StepDurationUnits.Days },
          { value: 30, unit: StepDurationUnits.Days },
          { value: 45, unit: StepDurationUnits.Days },
          { value: 60, unit: StepDurationUnits.Days },
        ],
      },
    },
    generateLayoutComponent('Choose how customers will select their pause duration'),
    generateButtonLabelsComponent('Confirm button', 'Decline button'),
    generateResponseScreenComponent(),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [CustomStepCategoryEnum.AdjustShippingFrequency]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction to be displayed to users wishing to pause subscription.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.TimeOptions,
      layout: {
        title: 'Adjust Shipping Frequency',
        subtitle: 'Add frequency options and let customers choose how often they’d like to receive their subscription.',
        type: StepTimeOptionTypes.ShippingFreq,
        items: [
          { value: 15, unit: StepDurationUnits.Days },
          { value: 30, unit: StepDurationUnits.Days },
          { value: 45, unit: StepDurationUnits.Days },
          { value: 60, unit: StepDurationUnits.Days },
        ],
      },
    },
    generateLayoutComponent('Choose how customers will select their shipping frequency'),
    generateButtonLabelsComponent('Confirm button', 'Decline button'),
    generateResponseScreenComponent(),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [CustomStepCategoryEnum.ExtendTrial]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Tell your customer the offer available to them. Use our offer text or create your own.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.TimeDelay,
      layout: {
        title: 'Offer a trial extension',
        subtitle: 'Example: Extend your trial by 7 days',
      },
    },
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.AdjustTrialFrequency]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction for customers wishing to change how often their trial bills.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.TimeOptions,
      layout: {
        title: 'Adjust Trial Frequency',
        subtitle: 'Add frequency options and let customers choose how often they’d like their trial to bill.',
        type: StepTimeOptionTypes.ShippingFreq,
        items: [
          { value: 45, unit: StepDurationUnits.Days },
          { value: 60, unit: StepDurationUnits.Days },
          { value: 90, unit: StepDurationUnits.Days },
          { value: 120, unit: StepDurationUnits.Days },
        ],
      },
    },
    generateLayoutComponent('Choose how customers will select their trial frequency'),
    generateButtonLabelsComponent('Confirm button', 'Decline button'),
    generateResponseScreenComponent(),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [CustomStepCategoryEnum.BillTrialNow]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Let customers start their subscription now and bill the first cycle immediately.',
      true
    ),
    generateMediaComponent(),
    generateButtonLabelsComponent('Confirm button', 'Decline button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.Testimonial]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short customer testimonial for the users to show the benefits'
    ),
    generateMediaComponent(),
    generateButtonLabelsComponent('Continue button', 'Cancel button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.LossAversion]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction to be displayed to users wishing to pause subscription.',
      true
    ),
    generateMediaComponent(),
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.OfferDetails]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Explain the offer to the customer.',
      true
    ),
    generateMediaComponent(),
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.ScheduleSupport]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'A short introduction to be displayed to users',
      true
    ),
    generateMediaComponent(),
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.ReviseOrder]: [
    generateTextComponent(
      'Title',
      'Tell your customer the offer available to them. Use our offer text or create your own.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    { type: StepElementTypes.Exchangeables },
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.ReturnOrderRMA]: [
    generateTextComponent(
      'Title',
      'Tell your customer the offer available to them. Use our offer text or create your own.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.DiscountSettings,
      layout: {
        title: 'Refund Settings',
        subtitle: 'Offer customers a partial refund as an incentive to keep the item instead of returning it.',
      },
    },
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
  ],

  /* UPSELLS */
  [CustomStepCategoryEnum.UpsellOrder]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Explain the offer to the customer.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.DiscountSettings,
      layout: {
        title: 'Discount Settings',
        subtitle: 'Configure how much of a discount to apply and how it’s calculated for upcoming subscription cycles.',
      },
    },
    generateButtonLabelsComponent('Accept Button', 'Reject Button'),
    generateResponseScreenComponent(),
  ],
  [CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount]: [
    generateTextComponent(
      'Title',
      'Tell your customer the offer available to them. Use our offer text or create your own.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateMediaComponent(),
    {
      type: StepElementTypes.DiscountSettings,
      layout: {
        title: 'Discount Settings',
        subtitle: 'Configure how much of a discount to apply and how it’s calculated for upcoming subscription cycles.',
      },
    },
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],

  /* GENERIC STEPS */
  [FunnelInputTypeEnum.Confirmation]: [generateCancellationComponent()],

  // SINGLE STEP Offers
  [CustomStepCategoryEnum.ChangeVariant]: [
    generateTextComponent(
      'Title',
      'Tell your customer the offer available to them. Use our offer text or create your own.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button'),
    generateResponseScreenComponent(),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [CustomStepCategoryEnum.ReActivateSubscription]: [
    generateTextComponent(
      'Title',
      'Tell your customer the offer available to them. Use our offer text or create your own.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateButtonLabelsComponent('Accept offer button', null, true),
    generateResponseScreenComponent(),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [CustomStepCategoryEnum.SkipSubscription]: [
    generateTextComponent(
      'Title',
      'Tell your customer the offer available to them. Use our offer text or create your own.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateButtonLabelsComponent('Accept offer button', null, true),
    generateResponseScreenComponent(),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [FunnelInputTypeEnum.EnterTroubleshooter]: [
    {
      type: StepElementTypes.ResponseScreen,
      layout: {
        title: 'Response Settings',
        subtitle: 'Customize the message shown to the customer after the troubleshooter completes.',
        responseTitle: {
          title: 'Heading',
          subtitle: 'This is the heading shown to the customer after they complete the step.',
        },
        response: {
          title: 'Message',
          subtitle: 'Use this space to briefly explain what just happened and what the customer can expect next.',
        },
      },
    },
  ],
  // Generic response step (category INPUT_ACTION_STATUS) — edits the step's own label/content
  // (+ its single Continue button), same as the Cancellation Response editor.
  [FunnelInputTypeEnum.InputActionStatus]: [generateCancellationComponent()],
  [FunnelInputTypeEnum.ShipmentTracking]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Explain the offer to the customer.',
      true
    ),
    generateButtonLabelsComponent('Primary Button', ''),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [FunnelInputTypeEnum.ItemStatus]: [
    generateButtonLabelsComponent('Primary Button', ''),
    {
      type: StepElementTypes.AdvancedSettings,
      layout: {
        title: 'Advanced Settings',
      },
    },
  ],
  [FunnelInputTypeEnum.Choice]: [
    generateTextComponent(
      'Title',
      'This is the main heading customers will see.',
      'Description',
      'Let your customer know that your offer is worth staying for. They mean a lot to you.',
      true
    ),
    generateMediaComponent(),
    generateButtonLabelsComponent('Accept offer button', 'Reject offer button', true),
    generateResponseScreenComponent(),
  ],
};
